import React, { useContext, useEffect, useRef, useState } from 'react';
import { APIProvider, Map, Marker, useMap } from '@vis.gl/react-google-maps';
import { Box, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import config from '../config.json';
import locationImg from '../assets/location_img.svg';
import logoWithName from '../assets/logo_with_name.svg';
import { GlobalStateContext } from '../context/globalStateProvider';

const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 };
const ROUTE_REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const ENABLE_GOOGLE_ROUTES = false;

export const DEMO_TRACKING = {
  valetTripId: '98765432-10fe-dcba-9876-543210fedcba',
  vehicleServiceOrderId: '3a1b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d',
  tripType: 'RETRIEVAL',
  status: 'IN_PROGRESS',
  startLocation: { lat: 28.5672234, lng: 77.3955253 },
  // This is the previous route origin used only to reproduce an off-route update locally.
  routeOrigin: { lat: 28.5674327, lng: 77.3954674 },
  currentLocation: { lat: 28.5936561, lng: 77.3975708 },
  destination: { lat: 28.567614185917325, lng: 77.39258084128885 },
  etaSeconds: 120,
  heading: 45,
  parkingName: 'ParkSmart Demo',
  activityLabel: 'Vehicle Recalled',
  currentEvent: 'Recalled',
  recallConfig: {
    recallTimeInSeconds: 900,
    remainingSeconds: 120,
  },
  pendingMessage: 'Live tracking will begin shortly.',
};

const toCoordinate = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    if (value[0] === null || value[0] === undefined || value[1] === null || value[1] === undefined) {
      return null;
    }

    const lat = Number(value[0]);
    const lng = Number(value[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  const latitude = typeof value?.lat === 'function'
    ? value.lat()
    : value?.lat ?? value?.latitude;
  const longitude = typeof value?.lng === 'function'
    ? value.lng()
    : value?.lng ?? value?.longitude;
  if (latitude === null || latitude === undefined || latitude === '' || longitude === null || longitude === undefined || longitude === '') {
    return null;
  }

  const lat = Number(latitude);
  const lng = Number(longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const distanceInMeters = (firstPoint, secondPoint) => {
  const earthRadiusInMeters = 6371000;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(secondPoint.lat - firstPoint.lat);
  const longitudeDelta = toRadians(secondPoint.lng - firstPoint.lng);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(toRadians(firstPoint.lat))
    * Math.cos(toRadians(secondPoint.lat))
    * Math.sin(longitudeDelta / 2) ** 2;

  return 2 * earthRadiusInMeters * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const trimRouteToCurrentLocation = (routePath, currentLocation) => {
  if (!currentLocation || routePath.length < 2) return routePath;

  let closestPointIndex = -1;
  let closestDistance = Infinity;

  routePath.forEach((point, index) => {
    const routePoint = toCoordinate(point);
    if (!routePoint) return;

    const distance = distanceInMeters(currentLocation, routePoint);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestPointIndex = index;
    }
  });

  if (closestPointIndex === -1) return routePath;

  // Keep the raw GPS point for the marker and show only the road route still ahead.
  return [currentLocation, ...routePath.slice(closestPointIndex).map(toCoordinate).filter(Boolean)];
};

export const normalizeTrackingUpdate = (payload) => {
  const startLocation = toCoordinate(
    payload?.startLocation ?? payload?.start ?? payload?.pickupLocation
  );
  const currentLocation = toCoordinate(
    payload?.currentLocation ?? payload?.current ?? payload?.location
  );
  const destination = toCoordinate(
    payload?.destination ?? payload?.endLocation ?? payload?.end
  );
  const path = (payload?.path ?? payload?.route ?? [])
    .map(toCoordinate)
    .filter(Boolean);
  const heading = Number(payload?.heading ?? payload?.degree ?? payload?.bearing ?? 0);

  return {
    startLocation,
    currentLocation,
    destination,
    path,
    heading: Number.isFinite(heading) ? heading : 0,
    tripType: payload?.tripType,
  };
};

const vehicleIcon = (heading) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><g transform="rotate(${heading} 24 24)"><path d="M24 7 36 37 24 31 12 37 24 7Z" fill="#18CC69" stroke="#0EAB55" stroke-width="2"/></g></svg>`;
  const icon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
  };

  // GPS coordinates represent the vehicle's center, not the image's bottom edge.
  if (window.google?.maps?.Point) {
    icon.anchor = new window.google.maps.Point(24, 24);
  }

  return icon;
};

const startLocationIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36"><circle cx="18" cy="18" r="15" fill="#0EAB55" stroke="#ffffff" stroke-width="3"/><text x="18" y="23" fill="#ffffff" font-family="Arial, sans-serif" font-size="16" font-weight="700" text-anchor="middle">S</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const TrackingPath = ({ path }) => {
  const map = useMap();
  const polylineRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    polylineRef.current = new window.google.maps.Polyline({
      map,
      strokeColor: '#18CC69',
      strokeOpacity: 1,
      strokeWeight: 5,
    });

    return () => polylineRef.current?.setMap(null);
  }, [map]);

  useEffect(() => {
    polylineRef.current?.setPath(path);
  }, [path]);

  return null;
};

const TrackingViewport = ({ tracking, path, isFullscreen }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const points = [
      ...path,
      tracking.startLocation,
      tracking.currentLocation,
      tracking.destination,
    ].filter(Boolean);

    if (points.length === 0) return;

    const fitTrackingBounds = () => {
      if (points.length === 1) {
        map.setCenter(points[0]);
        map.setZoom(16);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      // Keep the complete route visible while making it the focus of the compact card.
      map.fitBounds(bounds, isFullscreen ? 32 : 16);
    };

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(16);
      return;
    }

    fitTrackingBounds();
    const idleListener = window.google.maps.event.addListenerOnce(map, 'idle', fitTrackingBounds);
    return () => idleListener.remove();
  }, [isFullscreen, map, path, tracking]);

  return null;
};

const GoogleRoutePath = ({ origin, destination, onRoutePath }) => {
  const map = useMap();
  const lastRequestRef = useRef(null);

  useEffect(() => {
    if (!map || !origin || !destination) return;

    const destinationKey = `${destination.lat},${destination.lng}`;
    const lastRequest = lastRequestRef.current;
    const isRouteStillFresh = lastRequest
      && lastRequest.destinationKey === destinationKey
      && Date.now() - lastRequest.requestedAt < ROUTE_REFRESH_INTERVAL_MS;

    if (isRouteStillFresh) return;

    lastRequestRef.current = {
      destinationKey,
      requestedAt: Date.now(),
    };

    const loadRoute = async () => {
      try {
        const { Route } = await window.google.maps.importLibrary('routes');
        const { routes } = await Route.computeRoutes({
          origin,
          destination,
          travelMode: 'DRIVING',
          fields: ['path'],
        });

        const routePath = routes?.[0]?.path || [];
        if (!routePath.length) {
          console.warn('Google Routes API returned no route path.');
          return;
        }

        onRoutePath(routePath);
        console.info('Google route updated.', { pointCount: routePath.length });
      } catch (error) {
        console.error('Unable to load Google road route:', error);
      }
    };

    loadRoute();
  }, [destination, map, onRoutePath, origin]);

  return null;
};

const ArrivalCountdown = ({ remainingSeconds = 0 }) => {
  const [remainingTime, setRemainingTime] = useState(remainingSeconds);

  useEffect(() => {
    setRemainingTime(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (remainingTime <= 0) return undefined;

    const interval = window.setInterval(() => {
      setRemainingTime((time) => Math.max(0, time - 1));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [remainingTime]);

  const minutes = Math.ceil(remainingTime / 60);

  return (
    <Box sx={arrivalCountdownStyle}>
      <Box sx={arrivalTextStyle}>Arriving in ~</Box>
      <Box sx={countdownValueStyle}>{`${minutes} mins`}</Box>
    </Box>
  );
};

const LiveTrackingMap = ({
  tracking,
  startLocation,
  currentLocation,
  destination,
  path,
  heading,
  parkingName = '-',
  activityLabel,
  currentEvent,
  recallConfig,
  etaSeconds,
  pendingMessage = 'Live tracking will begin shortly.',
  useDemoData = false,
}) => {
  const { setPosition } = useContext(GlobalStateContext);
  const cardRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [googleRoutePath, setGoogleRoutePath] = useState([]);
  const displayParkingName = useDemoData ? DEMO_TRACKING.parkingName : parkingName;
  const displayActivityLabel = useDemoData ? DEMO_TRACKING.activityLabel : activityLabel;
  const displayCurrentEvent = useDemoData ? DEMO_TRACKING.currentEvent : currentEvent;
  const displayRecallConfig = useDemoData ? DEMO_TRACKING.recallConfig : recallConfig;
  const displayEtaSeconds = useDemoData ? DEMO_TRACKING.etaSeconds : etaSeconds;
  const displayPendingMessage = useDemoData ? DEMO_TRACKING.pendingMessage : pendingMessage;
  const suppliedTracking = tracking || {
    startLocation,
    currentLocation,
    destination,
    path,
    heading,
  };
  const mapTracking = normalizeTrackingUpdate(
    useDemoData ? DEMO_TRACKING : suppliedTracking
  );
  const hasLiveTracking = Boolean(
    displayCurrentEvent &&
    mapTracking.tripType === 'RETRIEVAL' &&
    mapTracking.startLocation &&
    mapTracking.destination
  );
  const fallbackRoutePath = mapTracking.path.length > 1
    ? mapTracking.path
    : [mapTracking.currentLocation, mapTracking.destination].filter(Boolean);
  const routePath = googleRoutePath.length > 1
    ? trimRouteToCurrentLocation(googleRoutePath, mapTracking.currentLocation)
    : fallbackRoutePath;
  const center = mapTracking.currentLocation ?? mapTracking.startLocation ?? mapTracking.destination ?? DEFAULT_MAP_CENTER;

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(() => {
    if (isFullscreen || !cardRef.current) return undefined;

    const updateBottomSheetPosition = () => {
      const rect = cardRef.current.getBoundingClientRect();
      setPosition(rect.bottom + window.pageYOffset);
    };

    updateBottomSheetPosition();
    window.addEventListener('resize', updateBottomSheetPosition);
    return () => window.removeEventListener('resize', updateBottomSheetPosition);
  }, [displayActivityLabel, displayCurrentEvent, isFullscreen, setPosition]);

  return (
    <Box ref={cardRef} sx={isFullscreen ? fullscreenContainerStyle : cardContainerStyle}>
      {!isFullscreen && (
        <>
          <Box sx={headerStyle}>
            <img src={logoWithName} alt="ParkSmart" width={80} height={30} />
            <Box sx={parkingNameStyle}>
              <img src={locationImg} alt="Location" width={15} height={15} />
              <Box sx={parkingLabelStyle}>{displayParkingName}</Box>
            </Box>
          </Box>
          {displayCurrentEvent === 'Recalled'
            ? <ArrivalCountdown
                remainingSeconds={displayEtaSeconds !== null && displayEtaSeconds !== undefined && Number.isFinite(Number(displayEtaSeconds))
                  ? Number(displayEtaSeconds)
                  : displayRecallConfig?.remainingSeconds || 0}
              />
            : displayActivityLabel && <Box sx={activityLabelStyle}>{displayActivityLabel}</Box>
          }
        </>
      )}
      <Box sx={isFullscreen ? fullscreenMapAreaStyle : mapAreaStyle}>
        {hasLiveTracking ? <>
          <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
            <Map
              defaultCenter={center}
              defaultZoom={16}
              gestureHandling="greedy"
              disableDefaultUI
              draggable
              keyboardShortcuts={false}
              scrollwheel
              style={{ width: '100%', height: '100%' }}
            >
              <TrackingViewport
                tracking={mapTracking}
                path={routePath}
                isFullscreen={isFullscreen}
              />
              {ENABLE_GOOGLE_ROUTES && (
                <GoogleRoutePath
                  origin={useDemoData
                    ? DEMO_TRACKING.routeOrigin
                    : mapTracking.currentLocation ?? mapTracking.startLocation}
                  destination={mapTracking.destination}
                  onRoutePath={setGoogleRoutePath}
                />
              )}
              {routePath.length > 1 && <TrackingPath path={routePath} />}
              {mapTracking.startLocation && (
                <Marker
                  position={mapTracking.startLocation}
                  icon={{ url: startLocationIcon() }}
                  title="Start location"
                />
              )}
              {mapTracking.destination && <Marker position={mapTracking.destination} title="Destination" />}
              {mapTracking.currentLocation && (
                <Marker
                  position={mapTracking.currentLocation}
                  icon={vehicleIcon(mapTracking.heading)}
                  title="Vehicle location"
                  zIndex={2}
                />
              )}
            </Map>
          </APIProvider>
          <IconButton
            aria-label={isFullscreen ? 'Exit full screen map' : 'Expand map'}
            onClick={() => setIsFullscreen((value) => !value)}
            sx={expandButtonStyle}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </> : (
          <Box sx={trackingPendingStateStyle}>
            <Box sx={trackingPendingMessageStyle}>
              {displayPendingMessage}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const cardContainerStyle = {
  padding: '16px 16px 0',
  backgroundColor: '#FAFAFA',
};

const fullscreenContainerStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 10001,
  backgroundColor: '#E8F0E9',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
};

const parkingNameStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
};

const parkingLabelStyle = {
  color: '#192A3E',
  fontSize: '14px',
  fontWeight: 600,
  marginLeft: '4px',
  maxWidth: '100px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const activityLabelStyle = {
  color: '#292929',
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '10px',
  textAlign: 'center',
};

const arrivalCountdownStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  marginBottom: '10px',
};

const arrivalTextStyle = {
   color: '#292929',
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '10px',
  textAlign: 'center',
};

const countdownValueStyle = {
  color: '#0EAB55',
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '10px',
  textAlign: 'center',
};

const mapAreaStyle = {
  height: '152px',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '12px',
  backgroundColor: '#E8F0E9',
};

const fullscreenMapAreaStyle = {
  width: '100%',
  height: '100%',
  position: 'relative',
  backgroundColor: '#E8F0E9',
};

const trackingPendingStateStyle = {
  alignItems: 'center',
  backgroundColor: '#F1F5F2',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  padding: '20px',
};

const trackingPendingMessageStyle = {
  '@keyframes trackingShimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  animation: 'trackingShimmer 2s linear infinite',
  background: 'linear-gradient(90deg, #FFFFFF 25%, #E4F5EA 50%, #FFFFFF 75%)',
  backgroundSize: '200% 100%',
  border: '1px solid #D7E9DD',
  borderRadius: '12px',
  boxShadow: '0 6px 16px rgba(25, 42, 62, 0.08)',
  color: '#192A3E',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.45,
  maxWidth: '300px',
  padding: '16px',
  textAlign: 'center',
};

const expandButtonStyle = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  zIndex: 2,
  width: '40px',
  height: '40px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 3px 12px rgba(25, 42, 62, 0.24)',
  color: '#192A3E',
  '&:hover': { backgroundColor: '#FFFFFF' },
};

export default LiveTrackingMap;
